import NoDraftRevisionError from '@/app/features/reports/components/draft-revision-error';
import ReportViewLoadingSkeleton from '@/app/features/reports/components/loading-state';
import { ReportGroupingProvider } from '@/app/features/reports/contexts/grouping-context';
import useReportData from '@/app/features/reports/hooks/useReportData';
import type { ReportFilters } from '@/app/features/reports/types/report-data';
import ErrorState from '@/components/raytd/error-state';
import React, { createContext, useEffect, useMemo, useState } from 'react';
import { ReportEntity } from 'store/src/lib/services/types';
import isSdaFormatReport from '../utils/isSdaFormatReport';
import { TestSuite } from 'store/src/lib/tests/entity';
import ReportDisplayProvider, { ReportDisplayContext } from '../contexts/display-context';
import { ReportSelectionProvider } from '../contexts/selection-context';
import { BookMark, ReportGroupModes } from '../types';
import { SelectedFilters } from '../types/filters';
import { MissingData } from '../types/missing-data';
import { ReportItem, ReportTestItem } from '../utils/entities';
import { useReportContext } from '@/app/features/reports/contexts/report-context';

export const useReportDisplayContext = () => {
    return React.useContext(ReportDisplayContext);
}

type ReportViewerContextValue = {
    reportId: number | null;
    activeItems: string[];
    setActiveItems: (items: string[]) => void;
    setUpdatingTest?: (id: number) => void;
    clearUpdatingTest?: () => void;
    isUpdatingTest?: boolean;
    updatingTestId?: number;
    bulkUpdatingTestIds?: Set<number>;
    setBulkUpdatingTestIds?: (ids: Set<number>) => void;
    suspendUnsavedGuard?: boolean;
    setSuspendUnsavedGuard?: (value: boolean) => void;
    isScrolling?: boolean;
    showTestEditor?: (id: number) => void;
    showFastFillQA?: boolean;
    showMissingData?: boolean;
    showStatus?: boolean;
    setShowFastFillQA?: (show: boolean) => void;
    setShowMissingData?: (show: boolean) => void;
    setShowStatus?: (show: boolean) => void;
    scrollToTest?: (id: number) => void;
    readOnly: boolean;
};

//create context for report viewer
export const ReportViewerContext = createContext<ReportViewerContextValue>({
    reportId: null,
    readOnly: false,
    activeItems: [],
    setActiveItems: () => {},
    setUpdatingTest: () => {},
    clearUpdatingTest: () => {},
    showMissingData: false,
    showFastFillQA: false,
    setShowMissingData: () => {},
    setShowFastFillQA: () => {},
    showStatus: false,
    setShowStatus: () => {},
    bulkUpdatingTestIds: new Set(),
    setBulkUpdatingTestIds: () => {},
});

const useReportViewerContext = () => {
    return React.useContext(ReportViewerContext);
}

export default useReportViewerContext;

// Main context for core report data and state
export interface ReportViewContext {
    rawTestItems: ReportTestItem[];
    filteredData: ReportTestItem[];
    testSuites: TestSuite[];
    data: ReportItem[];
    report: ReportEntity | null;
    reportTotal: number;
    visibleTestCount: number;
    missingData: MissingData | null;
    missingDataCount: number;
    missingSegments: number;
    bookmarks: BookMark[];
    isEditMode: boolean;
    setEditMode: (mode: boolean) => void;
    groupMode: ReportGroupModes;
    setGroupMode: (mode: ReportGroupModes) => void;
    isLoading: boolean;
    filters: ReportFilters | null;
    updateGroupedData: (groupMode: ReportGroupModes, data: ReportTestItem[]) => ReportTestItem[] | ReportItem[];
    viewId: string;
    // Derived once here from `report` so consumers share one source of truth instead of each
    // re-deriving isSdaFormatReport(report) independently (raytd/web#106).
    isSdaFormat: boolean;
    // Whether `groupMode` is a hard lock (the toolbar's grouping selector doesn't render at all,
    // e.g. a generated SDA-format Report, raytd/web QA 108.03) rather than just a switchable
    // default (an SDA-eligible Inspection's Category view, raytd/web QA 108.04) — these both set
    // `isSdaFormat` true for the shared compact-tile styling, but only the former should also
    // remove the user's ability to switch grouping.
    groupModeLocked: boolean;
}

export const defaultReportViewContext: ReportViewContext = {
    rawTestItems: [],
    filteredData: [],
    data: [],
    testSuites: [],
    report: null,
    reportTotal: 0,
    visibleTestCount: 0,
    missingData: null,
    missingDataCount: 0,
    missingSegments: 0,
    bookmarks: [],
    isEditMode: false,
    setEditMode: () => { },
    groupMode: 'location',
    setGroupMode: () => { },
    isLoading: false,
    filters: null,
    updateGroupedData: () => [],
    viewId: 'default',
    isSdaFormat: false,
    groupModeLocked: false,
}

export const ReportViewContext = createContext<ReportViewContext>(defaultReportViewContext);

interface ReportViewProviderProps {
    children: React.ReactNode;
    reportId: string;
    viewId: string;
    LoadingComponent?: React.ComponentType<{ isLoading?: boolean }>;
    initialEditMode?: boolean;
}

export const ReportViewProvider: React.FC<ReportViewProviderProps> = ({ 
    children, 
    reportId, 
    viewId,
    initialEditMode = true,
    LoadingComponent = ReportViewLoadingSkeleton
 }) => {
    const { editMode, setEditMode, isEditable, isPublished, revisionId } = useReportContext();

    useEffect(() => {
        setEditMode(initialEditMode && isEditable);
    }, [initialEditMode, isEditable, setEditMode]);

    // A user-chosen override, kept separate from the Format-conditional default below so the
    // default can be derived at render time from `report` (once it loads) rather than fixed by a
    // useState initializer at mount, when `report` may still be null — an effect correcting it
    // afterward would flash the app-wide default first (QA acceptance criterion, raytd/web#84).
    const [groupModeOverride, setGroupModeOverride] = useState<ReportGroupModes | null>(null);

    const reportID = isPublished ? revisionId?.toString() : String(reportId);

    const {
        enrichedTestItems: rawTestItemsData,
        filteredData,
        report,
        testSuites,
        isLoading,
        error,
        filters,
        bookmarks,
        missingData,
        missingDataCount,
        missingSegments,
        updateGroupedData,
    } = useReportData(
        reportID,
        !editMode,
        viewId
    );

    const isSdaFormat = isSdaFormatReport(report);
    const defaultGroupMode: ReportGroupModes = 'location';
    // SDA reports are FORCED into Category grouping (raytd/web QA 108.03, item 2) — not just
    // defaulted, so a user-chosen override picked before navigating here (or a stray
    // programmatic setGroupMode call) can never leak a different grouping into an SDA report.
    // The toolbar's group-mode selector is removed entirely for SDA for the same reason
    // (report-editing-toolbar.tsx).
    const groupMode = isSdaFormat ? 'category' : (groupModeOverride ?? defaultGroupMode);
    const setGroupMode = setGroupModeOverride;

    // ReportViewProvider isn't remounted when navigating between two different reports under the
    // same route (no `key`), so a user-chosen override picked on one report would otherwise leak
    // into the next report's initial view, masking its own Format-conditional default — the
    // isLoading gate below re-shows the skeleton for this same window, so this doesn't reintroduce
    // the flash the override pattern above was built to avoid.
    useEffect(() => {
        setGroupModeOverride(null);
    }, [reportID]);

    const data = useMemo(() => {
        const groupedData = updateGroupedData(groupMode, filteredData);
        return groupedData as ReportItem[];
    }, [filteredData, groupMode, updateGroupedData]);

    const value = useMemo(() => ({
        rawTestItems: rawTestItemsData ?? [],
        data,
        filteredData,
        report,
        testSuites,
        visibleTestCount: filteredData?.length ?? 0,
        reportTotal: rawTestItemsData?.length ?? 0,
        bookmarks,
        updateGroupedData,
        filters,
        isEditMode: editMode,
        setEditMode,
        groupMode,
        setGroupMode,
        isLoading,
        error,
        missingData,
        missingDataCount,
        missingSegments,
        viewId,
        isSdaFormat,
        groupModeLocked: isSdaFormat,
    }), [
        rawTestItemsData,
        filteredData,
        report,
        filters,
        editMode,
        groupMode,
        data,
        testSuites,
        isLoading,
        error,
        setEditMode,
        setGroupMode,
        bookmarks,
        updateGroupedData,
        missingData,
        missingDataCount,
        missingSegments,
        viewId,
        isSdaFormat
    ]);
    
    if (error) {
        const errorObj = error as { status?: number; data?: { errorCode?: string } };
        if (errorObj?.status === 404 && errorObj?.data?.errorCode === 'NO_DRAFT_REVISION') {
            return <NoDraftRevisionError />;
        } else {
            console.error('Error loading report', error);
            return <ErrorState message="Error loading report" />;
        }
    }

    if (isLoading) {
        return <LoadingComponent isLoading={isLoading} />;
    }

    return (
        <ReportViewContext.Provider value={value}>
            {children}
        </ReportViewContext.Provider>
    );
};

export const useReportViewContext = () => {
    return React.useContext(ReportViewContext);
}